import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@1trading/react';
import { JsonTree } from '@/components/ui/json-tree';
import type { InstitutionRow, InstitutionSegment } from '@/services/api/institutions';
import { stringifyValue } from '@/utils/format';

const SegmentTable = ({ segment }: { segment: InstitutionSegment }) => (
  <section>
    <h3 className="mb-2 font-semibold">{segment.name}</h3>
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <tbody>
          {segment.fields.map((field) => (
            <tr className="border-b last:border-b-0" key={field.label}>
              <td className="w-72 border-r bg-muted/30 px-3 py-2 align-top text-muted-foreground">{field.label}</td>
              {/* undefined → empty cell; non-primitives (e.g. productSelections) → JSON string */}
              <td className="break-all px-3 py-2">{stringifyValue(field.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);
SegmentTable.displayName = 'SegmentTable';

interface InstitutionDetailDialogProps {
  institution: InstitutionRow;
  onClose: () => void;
}

export const InstitutionDetailDialog = ({ institution, onClose }: InstitutionDetailDialogProps) => (
  <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
    <DialogContent className="flex max-h-[85vh] w-[90vw] flex-col sm:max-w-[1000px]">
      <DialogHeader>
        <DialogTitle>View Institution Details for {institution.attachment?.companyName}</DialogTitle>
      </DialogHeader>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
        {/* Segments render in insertion order: the fixed four, then Directors, UBOs, Business Partners */}
        {institution.segments.map((segment) => (
          <SegmentTable key={segment.name} segment={segment} />
        ))}
        <section>
          <h3 className="mb-2 font-semibold">RAW Data</h3>
          {/* Intentional PII exposure for compliance ops — full attachment incl. directors/UBOs */}
          <div className="overflow-x-auto rounded-md border bg-muted/40 p-3 font-mono text-xs">
            <JsonTree data={institution} />
          </div>
        </section>
      </div>
    </DialogContent>
  </Dialog>
);
InstitutionDetailDialog.displayName = 'InstitutionDetailDialog';
